import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/opt/buildhome/repo/packages/paste-website/src/layouts/DefaultLayout.tsx";
import { graphql } from 'gatsby';
import { Anchor } from '@twilio-paste/anchor';
import { Box } from '@twilio-paste/box';
import { Button } from '@twilio-paste/button';
import { Paragraph } from '@twilio-paste/paragraph';
import { Table, THead, TBody, Td, Th, Tr } from '@twilio-paste/table';
import { useTabState, Tabs, Tab, TabList, TabPanels, TabPanel } from '@twilio-paste/tabs';
import Changelog from '@twilio-paste/tabs/CHANGELOG.md';
import { MediaObject, MediaFigure, MediaBody } from '@twilio-paste/media-object';
import { InformationIcon } from '@twilio-paste/icons/esm/InformationIcon';
import { LinkExternalIcon } from '@twilio-paste/icons/esm/LinkExternalIcon';
import { Text } from '@twilio-paste/text';
import { useUID } from '@twilio-paste/uid-library';
import { UnorderedList, ListItem } from '@twilio-paste/list';
import { Callout, CalloutTitle, CalloutText } from '../../../components/callout';
import { SidebarCategoryRoutes } from '../../../constants';
import { HorizontalTabsExample, FittedTabsExample, StateHookTabsExample } from '../../../component-examples/TabsExamples';
export const pageQuery = graphql`
  {
    allPasteComponent(filter: {name: {eq: "@twilio-paste/tabs"}}) {
      edges {
        node {
          name
          description
          status
          version
        }
      }
    }
    mdx(fields: {slug: {eq: "/components/tabs/"}}) {
      fileAbsolutePath
      frontmatter {
        slug
        title
      }
      headings {
        depth
        value
      }
    }
  }
`;
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const ComponentHeader = makeShortcode("ComponentHeader");
const PageAside = makeShortcode("PageAside");
const LivePreview = makeShortcode("LivePreview");
const ChangelogRevealer = makeShortcode("ChangelogRevealer");
const layoutProps = {
  pageQuery,
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">




    <ComponentHeader name={props.pageContext.frontmatter.title} categoryRoute={SidebarCategoryRoutes.COMPONENTS} githubUrl="https://github.com/twilio-labs/paste/tree/main/packages/paste-core/components/tabs" storybookUrl="/?path=/story/components-tabs--horizontal-tabs" data={props.data.allPasteComponent.edges} mdxType="ComponentHeader" />
    <hr></hr>
    <contentwrapper>
      <PageAside data={props.data.mdx} mdxType="PageAside" />
      <content>
        <Callout mdxType="Callout">
  <CalloutTitle mdxType="CalloutTitle">Using alpha components in your product</CalloutTitle>
  <CalloutText mdxType="CalloutText">
    Alpha components are ready for you to use in production. Components such as Tabs are likely to not see any API
    changes. This is only an alpha due to the lack of documentation and minimal design. Feel free to share your
    experience using this component at #help-design-system or{' '}
    <Anchor href="https://github.com/twilio-labs/paste/discussions/new" mdxType="Anchor">start a discussion on GitHub</Anchor>.
  </CalloutText>
        </Callout>
        <h2>{`Guidelines`}</h2>
        <h3>{`About Tabs`}</h3>
        <p>{`The Tabs component allows a user to flip through multiple views within the same page.`}</p>
        <h2>{`Examples`}</h2>
        <h3>{`Horizontal Tabs`}</h3>
        <LivePreview scope={{
          Tabs,
          Tab,
          TabList,
          TabPanels,
          TabPanel,
          useUID
        }} noInline mdxType="LivePreview">
  {HorizontalTabsExample}
        </LivePreview>
        <h3>{`Fitted Tabs`}</h3>
        <p>{`Fitted tabs allow the tabs to expand and fit the width of a containing element.`}</p>
        <LivePreview scope={{
          Tabs,
          Tab,
          TabList,
          TabPanels,
          TabPanel,
          useUID
        }} noInline mdxType="LivePreview">
  {FittedTabsExample}
        </LivePreview>
        <h3>{`Using state hooks`}</h3>
        <Callout variant="warning" mdxType="Callout">
  <CalloutTitle mdxType="CalloutTitle">🚨 ATTENTION!! 🚨</CalloutTitle>
  <CalloutText mdxType="CalloutText">
    If you decided to use state hooks, the Paste team is <strong>NOT</strong> responsible for debugging any issues in
    your application.
  </CalloutText>
        </Callout>
        <p>{`The `}<inlineCode parentName="p">{`state`}</inlineCode>{` prop allows you to hook into, and keep in sync the state of the Tabs and the state of your application.`}</p>
        <p>{`This will allow you to set initial state and merge the returned state from the hook with the application state,
and keep it in sync with user interactions.`}</p>
        <p>{`You could use this if you want to add functionality to a button within the tab content. In the example below we're
using the `}<inlineCode parentName="p">{`select`}</inlineCode>{` function from the tab state to advance to the next tab, and go back to the previous tab.`}</p>
        <LivePreview scope={{
          useTabState,
          Tabs,
          Tab,
          TabList,
          TabPanels,
          TabPanel,
          Button,
          Paragraph
        }} noInline language="jsx" mdxType="LivePreview">
  {StateHookTabsExample}
        </LivePreview>
        <h2>{`Anatomy`}</h2>
        <Box marginBottom="space60" mdxType="Box">
  <Table tableLayout="fixed" mdxType="Table">
    <THead mdxType="THead">
      <Tr mdxType="Tr">
        <Th mdxType="Th">Property</Th>
        <Th mdxType="Th">Default token</Th>
        <Th mdxType="Th">Modifiable?</Th>
      </Tr>
    </THead>
    <TBody mdxType="TBody">
      <Tr mdxType="Tr">
        <Td mdxType="Td">Tab label text</Td>
        <Td mdxType="Td">
          <UnorderedList marginBottom="space0" mdxType="UnorderedList">
            <ListItem mdxType="ListItem">Always: $font-size-30, $font-weight-semibold, $line-height-20</ListItem>
            <ListItem mdxType="ListItem">Default: $color-text-weak, on hover: $color-text</ListItem>
            <ListItem mdxType="ListItem">Selected: $color-text-link</ListItem>
            <ListItem mdxType="ListItem">Disabled: $color-text-weaker</ListItem>
          </UnorderedList>
        </Td>
        <Td mdxType="Td">No</Td>
      </Tr>
      <Tr mdxType="Tr">
        <Td mdxType="Td">Tab Label box</Td>
        <Td mdxType="Td">
          <UnorderedList marginBottom="space0" mdxType="UnorderedList">
            <ListItem mdxType="ListItem">border-bottom (selected tab): $color-border-primary</ListItem>
            <ListItem mdxType="ListItem">border-bottom (on hover): $color-border-primary-darker</ListItem>
            <ListItem mdxType="ListItem">
              Horizontal Tabs
              <UnorderedList marginBottom="space0" mdxType="UnorderedList">
                <ListItem mdxType="ListItem">padding-top: $space-40</ListItem>
                <ListItem mdxType="ListItem">padding-right: $space-20</ListItem>
                <ListItem mdxType="ListItem">padding-bottom: $space-40</ListItem>
                <ListItem mdxType="ListItem">padding-left: $space-20</ListItem>
                <ListItem mdxType="ListItem">margin-right: $space-70 (except for last tab)</ListItem>
              </UnorderedList>
            </ListItem>
            <ListItem mdxType="ListItem">
              Fitted Tabs
              <UnorderedList marginBottom="space0" mdxType="UnorderedList">
                <ListItem mdxType="ListItem">padding-top: $space-40</ListItem>
                <ListItem mdxType="ListItem">padding-right: $space-20</ListItem>
                <ListItem mdxType="ListItem">padding-bottom: $space-40</ListItem>
                <ListItem mdxType="ListItem">padding-left: $space-20</ListItem>
              </UnorderedList>
            </ListItem>
          </UnorderedList>
        </Td>
        <Td mdxType="Td">No</Td>
      </Tr>
      <Tr mdxType="Tr">
        <Td mdxType="Td">Tab List Border</Td>
        <Td mdxType="Td">
          <UnorderedList marginBottom="space0" mdxType="UnorderedList">
            <ListItem mdxType="ListItem">Always: $color-border-light</ListItem>
          </UnorderedList>
        </Td>
        <Td mdxType="Td">No</Td>
      </Tr>
    </TBody>
  </Table>
        </Box>
        <hr></hr>
        <h2>{`Usage Guide`}</h2>
        <h3>{`API`}</h3>
        <h4>{`Installation`}</h4>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`yarn add @twilio-paste/tabs - or - yarn add @twilio-paste/core
`}</code></pre>
        <h4>{`Usage`}</h4>
        <pre><code parentName="pre" {...{
            "className": "language-jsx"
          }}>{`import {Tabs, TabList, Tab, TabPanels, TabPanel} from '@twilio-paste/tabs';

const HorizontalTabsExample: React.FC = () => {
  const selectedId = useUID();
  return (
    <Tabs selectedId={selectedId} baseId="horizontal-tabs-example">
      <TabList aria-label="My tabs">
        <Tab>Tab 1</Tab>
        <Tab disabled>Tab 2</Tab>
        <Tab id={selectedId}>Tab 3</Tab>
      </TabList>
      <TabPanels paddingTop="space20">
        <TabPanel>Tab 1</TabPanel>
        <TabPanel>Tab 2</TabPanel>
        <TabPanel>Tab 3</TabPanel>
      </TabPanels>
    </Tabs>
  );
};
`}</code></pre>
        <h4>{`Tabs Props`}</h4>
        <h5><inlineCode parentName="h5">{`variant?: 'fitted' | undefined (default)`}</inlineCode></h5>
        <p>{`Changes the Tabs' to either fit the width of the containing element or not.`}</p>
        <h5><inlineCode parentName="h5">{`selectedId?: string | null`}</inlineCode></h5>
        <p>{`Tells the Tabs which panel to load when it mounts. Important to provide, otherwise the first render may be
a little delayed.`}</p>
        <h5><inlineCode parentName="h5">{`baseId?: string`}</inlineCode></h5>
        <p>{`Sets the prefix for the auto-generated ids on each Tab and TabPanel. Useful for consistent renders, like for tests.`}</p>
        <h4>{`TabList Props`}</h4>
        <h5><inlineCode parentName="h5">{`aria-label: string`}</inlineCode></h5>
        <p>{`Required label for this Tabs component. Titles the entire Tabbing context for screen readers.`}</p>
        <h4>{`Tab Props`}</h4>
        <h5><inlineCode parentName="h5">{`id?: string | undefined`}</inlineCode></h5>
        <p>{`Same as the HTML attribute.`}</p>
        <h5><inlineCode parentName="h5">{`focusable?: boolean | undefined`}</inlineCode></h5>
        <p>{`When an element is disabled, it may still be focusable.
It works similarly to readOnly on form elements. In this case, only aria-disabled will be set.`}</p>
        <h5><inlineCode parentName="h5">{`disabled?: boolean | undefined`}</inlineCode></h5>
        <p>{`Same as the HTML attribute.`}</p>
        <h4>{`TabPanels Props`}</h4>
        <p>{`None available.`}</p>
        <h4>{`TabPanel Props`}</h4>
        <h5><inlineCode parentName="h5">{`id?: string | undefined`}</inlineCode></h5>
        <p>{`Same as the HTML attribute.`}</p>
        <h5><inlineCode parentName="h5">{`tabId?: string | undefined`}</inlineCode></h5>
        <p>{`The ID of the Tab component this Panel pairs with.`}</p>
        <ChangelogRevealer mdxType="ChangelogRevealer">
  <Changelog mdxType="Changelog" />
        </ChangelogRevealer>
      </content>
    </contentwrapper>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      